import { openDB, IDBPDatabase } from 'idb';

export interface OcrWordData {
    text: string;
    bbox: { x0: number; y0: number; x1: number; y1: number };
}

export interface CachedOcrPage {
    bookId: string;
    pageNumber: number;
    viewportWidth: number;
    viewportHeight: number;
    words: OcrWordData[];
    timestamp: number;
}

const DB_NAME = 'libritui_ocr_cache_v2';
const STORE_NAME = 'ocr_pages';

class OcrCacheService {
    private dbPromise: Promise<IDBPDatabase<any>> | null = null;

    constructor() {
        if (typeof window !== 'undefined') {
            this.initDb();
        }
    }

    private initDb() {
        this.dbPromise = openDB(DB_NAME, 1, {
            upgrade(db) {
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    const store = db.createObjectStore(STORE_NAME, { keyPath: ['bookId', 'pageNumber'] });
                    // Optional index for getting all pages of a book if needed
                    store.createIndex('by_bookId', 'bookId');
                }
            },
        });
    }

    async getCachedPage(bookId: string, pageNumber: number): Promise<CachedOcrPage | null> {
        if (!this.dbPromise) return null;
        try {
            const db = await this.dbPromise;
            const cached = await db.get(STORE_NAME, [bookId, pageNumber]);
            return cached || null;
        } catch (err) {
            console.warn('Failed to read from OCR cache:', err);
            return null;
        }
    }

    async saveCachedPage(data: CachedOcrPage): Promise<void> {
        if (!this.dbPromise) return;
        try {
            const db = await this.dbPromise;
            await db.put(STORE_NAME, data);
        } catch (err) {
            console.warn('Failed to write to OCR cache:', err);
        }
    }

    async clearBookCache(bookId: string): Promise<void> {
        if (!this.dbPromise) return;
        try {
            const db = await this.dbPromise;
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const index = tx.store.index('by_bookId');

            let cursor = await index.openCursor(IDBKeyRange.only(bookId));
            while (cursor) {
                await cursor.delete();
                cursor = await cursor.continue();
            }
            await tx.done;
        } catch (err) {
            console.warn('Failed to clear book cache:', err);
        }
    }
}

export const ocrCacheService = new OcrCacheService();
